import { useEffect, useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { addSearchEntry } from 'lib/reader/analytics';
import { SearchForm, LoadingIndicator } from 'components/common';
import { EditorSearchLocal } from '.';

import { FormApi } from 'informed';
import { EditorSearchData } from './EditorSearchLocal';

interface Props {
  disabled?: boolean;
  onSearch: (state: boolean) => void;
}

const EditorSearch = ({ disabled, onSearch }: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig,
    theme,
    user
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState<EditorSearchData[]>(null);
  const [searchString, setSearchString] = useState('');

  useEffect(() => {
    if (!searchString) handleClear();
  }, [searchString]);

  const handleClear = () => {
    setSearchData(null);
    setSearchString('');
    onSearch(false);
  };

  const handleError = (err: Error) => dispatch.handleErrorDispatch(err);

  const handleSearchStringChange = (value: string) => setSearchString(value);

  const handleSearch = async (searchString: string) => {
    if (!searchString) {
      setLoading(false);
      setSearchData(null);
      return;
    }

    try {
      setLoading(true);

      addSearchEntry({
        searchString,
        timeZone: serverConfig.timeZone,
        type: 'local',
        courseId: course.id,
        userId: user.id
      });

      const searchData = await searchLocal(searchString.toLowerCase());
      setSearchData(searchData);
    } catch (err) {
      console.error(err);
      handleClear();
    } finally {
      setLoading(false);
    }
  };

  const searchLocal = async (searchString: string) => {
    onSearch(true);

    const response = await api.post(
      `/admin/courses/${course.id}/chapters/search`,
      { searchString, isEditorSearch: true }
    );
    if (!response.data) return [];
    return response.data.results;
  };

  return (
    <>
      <div className="search-form">
        <SearchForm
          disabled={disabled}
          onClear={handleClear}
          onChange={handleSearchStringChange}
          onError={handleError}
          onSearch={handleSearch}
          placeholderText={messages.commonSearch}
          searchString={searchString}
          setFormApi={(form: FormApi) => (formApi.current = form)}
        />

        {searchData ? (
          <div className="search-info">
            {!searchData.length ? (
              <p className="results-found">{messages.readerSearchNoResults}</p>
            ) : (
              <p className="results-found">{`${searchData.length} ${messages.readerSearchResults}`}</p>
            )}
            <button className="search-clear" onClick={handleClear}>
              {messages.readerSearchClearResults}
            </button>
          </div>
        ) : null}

        <style jsx>{`
          .search-form {
            padding: 10px;
          }
          .search-info {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 1.4rem;
            padding: 10px 3px 0px 3px;
          }
          .results-found {
            color: ${theme.textColorLighter};
            margin: 0;
          }
          .search-clear {
            font-family: inherit;
            font-size: 1.4rem;
            padding: 0;
            margin: 0;
            height: 20px;
            border: 0;
            background-color: inherit;
            cursor: pointer;
            color: ${theme.dangerColor};
          }
          .search-clear:focus,
          .search-clear:hover {
            text-decoration: underline;
          }
        `}</style>
      </div>

      {loading ? (
        <LoadingIndicator />
      ) : searchData?.length > 0 ? (
        <EditorSearchLocal data={searchData || []} />
      ) : null}
    </>
  );
};

export default EditorSearch;
